'use client';

import { LDProvider } from 'launchdarkly-react-client-sdk';
import { type PropsWithChildren } from 'react';

import { flagsContext } from '@/contexts/flagsContext';

import { useEnvironment } from './useEnvironment';
import { useUser } from './useUser';

export const LaunchDarklyProvider = (props: PropsWithChildren) => {
  const { isDev, isDemo, isTest, launchDarklyClientId } = useEnvironment();
  const { currentProjectId, user } = useUser();
  if (!currentProjectId) {
    return props.children;
  }

  if (isDemo || isDev || isTest) {
    return (
      <flagsContext.Provider
        value={{
          flags: {
            // Amir
            NonCognitoEonAccounts: false,
            SkipOnboarding: false,
            dlsgFrontend: false,
            LlgFrontend: false,
            InventoryStatusColumns: isDev,
            GcpPermissionGroupEnforcement: true,
            AwsPermissionGroupEnforcement: true,
            // LakehouseBaseUrl: 'http://localhost:3003',

            // Erez
            // permanent
            enableHourlyBackup: false, // opened only for Fanatics
            AzureSqlVirtualMachineSupport: true,
            AzureSqlManagedInstanceSupport: true,
            MySqlFlexibleServerSupport: true,
            ownCustomerOnboardingEnabled: false,
            hourlyDlsgPolicyEnabled: false,
            SecurityFindings: true,

            // Sivan
            AllowAccountLevelSamlWhenOrgAttached: false,

            // Matan
            InventoryFilterOmnibox: true,

            // Yaara
            EnableGDSGPage: isDev,
            EnableGdsgNotifications: false,
            StorageGatewayCostExplorer: isDev,
            CostExplorerUsdToOtherCurrencies: isDev,
            EnableGdsgObjectActions: false,
            EnableGdsgInventoryReports: isDev,
            SupportColdTierVaults: true,

            // Or
            AiAssistantSidebar: true,

            // Moshe
            SapHanaPitrSupport: true,
            MongoAtlasSupport: true,
            MongoAtlasNonAwsClusters: isDev,

            // Ilan
            MongoAtlasAdvancedRestoreEnabled: isDev,
            GcpSqlVirtualMachineSupport: true,

            // Ilya
            ExploreFilesForDatabaseVms: true,

            // Keren
            RdsRestoreWithoutEncryptionEnabled: false,
            RecordRestoreResultsEnabled: true,

            // Dvir
            GcpVmHighFrequencyBackupEnabled: true,

            // Abir
            RdsHighFrequencyBackupEnabled: true,

            // Nitzan
            // true matches the restore service's LocalValue; production defaults to false in
            // LaunchDarkly until the BigQuery/Kusto KV record-restore engines roll out.
            EnableCrossCloudKvRecordRestore: true,
            EnableCrossCloudKvRecordRestoreAzure: true,

            // Ariel
            AzureSnapshotsDiscoverySupport: true,

            // Eylon
            AzureAdeBackupSupport: true,
            AzureVmDesRestoreSupport: true,

            // Omer
            AwsUntaggedRecoveryPointDiscovery: true,

            // Barak
            AzureMssqlTdeCmkRestore: true,

            // Ran
            AwsRdsNativeBackupSupport: true,
            ShadowClassificationPolicy: true,
            AwsEksClusterBackupSupport: true,
            AwsEc2InAccountBackupSupport: true,
            // Tzachi
            AzureMultipleAppRegistrations: false,
            AzureCustomPermissionsModel: false,
            EnableAzurePostgresRestoreCreateNetwork: false,

            // Google Workspace
            GoogleWorkspaceSupport: true,
            GoogleWorkspaceEnableRansomwareDetection: true,
            GwsInPlaceRestoreOnly: false,
            GwsRestoreToTargetMailbox: true,
            GwsRestoreToTargetDrive: true,
            GwsRestoreWholeSharedDrive: true,

            // Microsoft 365
            Microsoft365Support: true,
            Microsoft365RestoreSupport: true,
            EnableRansomwareDetectionM365: true,
            M365TeamsSupport: true,
            // Off in demo: its fixtures have no chats, so every user would show an empty Chats row.
            M365TeamsChatsSupport: !isDemo,
            M365RestoreToAnotherUser: true,

            // Entra ID (IDP track)
            EntraIdSupport: true,
            EntraIdApplicationEstate: true,
            EntraIdAdministrativeUnits: false,

            // Shirly
            UnifiedSearch: isDev || isDemo,

            // Eden
            EnableMrapForByov: true,

            // Oryan
            BigQueryNativeRestoreEnabled: true,
            EnableBigQueryNativePolicy: true,
            GcpProjectLabelTagFilterEnabled: true,
            CancelBackupJobEnabled: true,
            BigQueryRestoreOverwriteEnabled: true,

            // Benji
            SensitivityAnnotations: true,
            EnableNetworkClassification: true,

            // Liran - RDS Restore
            RdsRestorePreserveOriginalMemberNames: false,
            CassandraSupport: true,

            // Liran - StreamLake
            DataPlatformConsoleEnabled: true,
            DataEngineDefaultLanding: false,

            // MatanR
            RdsCdcUiEnabled: true,

            // MatanD
            LakehouseMetricExportEnabled: true,
            LakehouseSqlAssistant: true,

            // Eyal
            RolePermissionFamiliesEnabled: true,
            UnifiedIdentityRestore: true,

            // Or
            AiAgents: isDev,
            AiDashboardsSupport: true,
            AiAgentCostExplorer: isDev,

            // Yair
            ZeroEtlEnabled: true,
            LakehouseEnabled: false,
            ZeroEtlMultiResourcePolicies: false,
            ZeroEtlBackupPolicyLink: true,
            ZeroEtlIntegrationsEnabled: true,
            DataKnowledgeGraphEnabled: false,
            AgenticDataLayerEnabled: false,
            // Offers the AWS account ID input so a Glue catalog can be created
            // in a foreign account. Off means Eon-owned catalogs only.
            LakehouseCustomerOwnedCatalogs: false,
            // `true` offers every Lakehouse-backed Data Engine screen; an
            // array of screen keys offers exactly those. See
            // `libs/streamlake/screens.ts`.
            StreamlakeLakehouseScreens: true,
            // Storage / Spend charts on the pipeline overview: on for a local
            // dev server, off in demo and tests until the cost and table-size
            // gauges are trustworthy.
            StreamlakePipelineCostChartsEnabled: isDev,
            // The object-store request lines on those two charts, which are
            // metered and priced separately from capacity and roll out on their
            // own. Narrows the flag above rather than replacing it: the lines
            // are drawn on its charts, so both must be on to see them.
            StreamlakePipelineStorageAPICostChartsEnabled: false,
            StreamlakePipelineTuning: false,

            // Daniel - Export access-denied items
            EnableExportAccessDeniedItems: true,
            InventoryIssuesColumn: false,

            // Lior
            RestoreTemplates: true,
            BulkRestore: true,
            EnableYaraScan: true,
            RemediationOrchestration: true,

            // Jonathan - Inventory entities filter
            EnableInventoryEntitiesFilter: true,
            DataClassificationCredentialsEnabled: false,

            // Zohar - SaaS inventory default landing page
            SaasInventoryDefaultLanding: false,

            // Ilan K
            BackupComplianceJson: {
              view: true,
              trend: true,
              export: true,
              audit: false,
            },

            // Lior - Snapshot Architect
            EnableSnapshotArchitect: true,

            // Setter
            UseTargetVpcForRestoreServer: true,
            UseTargetVpcForFileRestoreServer: true,
            EnableGcpInstantRestore: true,
            ShovalCdcMode: { mode: 'agent_only' },

            // Assaf
            PolicyAndVaultMetadataLabels: true,
          },
          flagKeyMap: {},
          ldClient: undefined,
        }}
      >
        {props.children}
      </flagsContext.Provider>
    );
  }

  if (!launchDarklyClientId || !user?.eonAccount) {
    return props.children;
  }

  return (
    <LDProvider
      reactOptions={{
        reactContext: flagsContext,
        useCamelCaseFlagKeys: false,
      }}
      clientSideID={launchDarklyClientId}
      timeout={2}
      context={{
        kind: 'multi',
        user: {
          key: currentProjectId, // backward compatibility
          // name: user.givenName,
          // email: user.email,
          // id: user.id,
          name: user.eonAccount.name,
          domain: user.eonAccount.domain,
        },
        organization: {
          key: user.eonAccount.id,
          name: user.eonAccount.name,
          domain: user.eonAccount.domain,
          projectId: currentProjectId,
        },
      }}
      options={{ streaming: false }}
    >
      {props.children}
    </LDProvider>
  );
};
